import React, { useEffect, useMemo, useState } from "react";
import "../styleSheets/dataTable.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TooltipButton } from "../utilities/ToolTipButton";

type Align = "left" | "center" | "right";
type SortDirection = "asc" | "desc";

export type ColumnDef<T> = {
  /** Unique id for the column */
  id: string;

  /** Header label */
  header: React.ReactNode;

  /** Render cell content (can be text, JSX, icons, buttons, etc.) */
  cell: (row: T) => React.ReactNode;

  /** Column alignment */
  align?: Align;

  /** Optional width (e.g. "160px" or "20%") */
  width?: string;

  /**
   * Enable sorting on this column (STRING FIELDS ONLY).
   * Provide a function that returns the string value to sort by.
   * If omitted, column is not sortable.
   */
  sortValue?: (row: T) => string;

  /**
   * Enable searching on this column (STRING FIELDS ONLY).
   * Provide a function that returns the string value to search in.
   * If omitted, column is not searched.
   */
  searchValue?: (row: T) => string;

  /**
   * Mark this column as actions/controls (optional).
   * Purely used for styling & intent; not searched/sorted by default anyway.
   */
  isActions?: boolean;
};

export type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];

  /** Row key getter and onRow click */
  getRowKey?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;

  /** UI options */
  enableSearch?: boolean;
  searchPlaceholder?: string;
  enableAddNew?: boolean;
  addNewLabel?: string;
  addNewLink: string;
  disableAddNew?: boolean;
  disableAddNewTooltip?: string;

  pageSizeOptions?: number[];
  initialPageSize?: number;

  /** Initial sort must be a column that has sortValue */
  initialSort?: { columnId: string; direction: SortDirection };

  /** Empty states */
  emptyTitle?: string;
  emptyDescription?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
};

function norm(s: string) {
  return s.toLowerCase().trim();
}

function compareStrings(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
}

export function DataTable<T>({
  data,
  columns,
  getRowKey,
  onRowClick,

  enableSearch = true,
  enableAddNew,
  addNewLabel,
  addNewLink,
  disableAddNew,
  disableAddNewTooltip,
  searchPlaceholder,

  pageSizeOptions = [5, 10, 20, 50],
  initialPageSize = 10,

  initialSort,

  emptyTitle,
  emptyDescription,
  noResultsTitle,
  noResultsDescription,
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const searchPlaceholderRender =
    searchPlaceholder ?? t("common.table.searchPlaceholder");
  const emptyTitleRender = emptyTitle ?? t("common.table.emptyTitle");
  const emptyDescriptionRender =
    emptyDescription ?? t("common.table.emptyDescription");
  const noResultsTitleRender =
    noResultsTitle ?? t("common.table.noResultsTitle");
  const noResultsDescriptionRender =
    noResultsDescription ?? t("common.table.noResultsDescription");
  const [sort, setSort] = useState<{
    columnId: string;
    direction: SortDirection;
  } | null>(initialSort ?? null);

  // Only columns that explicitly provide searchValue are searched (string fields)
  const searchableColumns = useMemo(
    () => columns.filter((c) => typeof c.searchValue === "function"),
    [columns],
  );

  // Only columns that explicitly provide sortValue are sortable (string fields)
  const sortableColumns = useMemo(() => {
    const map = new Map<string, ColumnDef<T>>();
    columns.forEach((c) => map.set(c.id, c));
    return map;
  }, [columns]);

  // Filter
  const filtered = useMemo(() => {
    if (!enableSearch) return data;
    const q = norm(query);
    if (!q) return data;

    return data.filter((row) =>
      searchableColumns.some((col) => {
        const val = col.searchValue?.(row) ?? "";
        return norm(val).includes(q);
      }),
    );
  }, [data, enableSearch, query, searchableColumns]);

  // Sort (strings only)
  const sorted = useMemo(() => {
    if (!sort) return filtered;

    const col = sortableColumns.get(sort.columnId);
    if (!col?.sortValue) return filtered;

    const dir = sort.direction === "asc" ? 1 : -1;

    return [...filtered].sort((ra, rb) => {
      const a = col.sortValue?.(ra) ?? "";
      const b = col.sortValue?.(rb) ?? "";
      return compareStrings(a, b) * dir;
    });
  }, [filtered, sort, sortableColumns]);

  // When query or pageSize changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  // Pagination
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  const isEmpty = data.length === 0;
  const isNoResults = !isEmpty && filtered.length === 0;

  function toggleSort(columnId: string) {
    const col = sortableColumns.get(columnId);
    if (!col?.sortValue) return; // not sortable

    setPage(1);

    setSort((prev) => {
      if (!prev || prev.columnId !== columnId)
        return { columnId, direction: "asc" };
      if (prev.direction === "asc") return { columnId, direction: "desc" };
      return null; // third click clears sorting
    });
  }

  function addNewButtonClicked() {
    navigate(addNewLink, { state: null });
  }

  return (
    <div className="dt">
      <div className="dt__toolbar">
        {enableSearch && (
          <input
            className="dt__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholderRender}
            aria-label={t("common.table.searchTable")}
          />
        )}
        <div className="dt__toolbar-right">
          {enableAddNew && (
            <TooltipButton
              label={addNewLabel ?? t("common.table.addNew")}
              tooltip={disableAddNewTooltip}
              disabled={disableAddNew}
              onClick={addNewButtonClicked}
              buttonClassName="btn btn--orange btn--size-md"
            />
          )}
          <div className="dt__controls">
            <label className="dt__rowsLabel">
              {t("common.table.rows")}
              <select
                className="dt__select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label={t("common.table.rowsPerPage")}
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div />
      </div>

      <div className="dt__wrap">
        <table className="dt__table">
          <thead className="dt__thead">
            <tr>
              {columns.map((col) => {
                const sortable = !!col.sortValue;
                const active =
                  sort?.columnId === col.id ? sort.direction : null;

                return (
                  <th
                    key={col.id}
                    className={[
                      "dt__th",
                      sortable ? "dt__th--sortable" : "",
                      col.align ? `dt__cell--${col.align}` : "",
                      col.isActions ? "dt__th--actions" : "",
                    ].join(" ")}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => sortable && toggleSort(col.id)}
                    scope="col"
                    aria-sort={
                      active === "asc"
                        ? "ascending"
                        : active === "desc"
                          ? "descending"
                          : "none"
                    }
                    role={sortable ? "button" : undefined}
                    tabIndex={sortable ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!sortable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSort(col.id);
                      }
                    }}
                  >
                    <div className="dt__thInner">
                      <span>{col.header}</span>
                      {sortable && (
                        <span className="dt__sortIcon" aria-hidden="true">
                          {active === "asc"
                            ? "▲"
                            : active === "desc"
                              ? "▼"
                              : "↕"}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {isEmpty && (
              <tr>
                <td className="dt__emptyCell" colSpan={columns.length}>
                  <div className="dt__empty">
                    <div className="dt__emptyTitle">{emptyTitleRender}</div>
                    <div className="dt__emptyDesc">
                      {emptyDescriptionRender}
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {isNoResults && (
              <tr>
                <td className="dt__emptyCell" colSpan={columns.length}>
                  <div className="dt__empty">
                    <div className="dt__emptyTitle">{noResultsTitleRender}</div>
                    <div className="dt__emptyDesc">
                      {noResultsDescriptionRender}
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {!isEmpty &&
              !isNoResults &&
              paged.map((row, idx) => {
                const clickable = typeof onRowClick === "function";
                return (
                  <tr
                    key={getRowKey ? getRowKey(row, idx) : String(idx)}
                    className={`dt__tr ${clickable ? "dt__tr--clickable" : ""}`}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={[
                          "dt__td",
                          col.align ? `dt__cell--${col.align}` : "",
                          col.isActions ? "dt__td--actions" : "",
                        ].join(" ")}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="dt__footer">
        <div className="dt__meta">
          {total > 0 && !isNoResults ? (
            <span className="dt__count">
              {(safePage - 1) * pageSize + 1}–
              {Math.min(safePage * pageSize, total)} {t("common.table.of")}{" "}
              {total}
            </span>
          ) : (
            <span className="dt__count">0</span>
          )}
        </div>

        <div className="dt__pager">
          <button
            className="dt__btn"
            onClick={() => setPage(1)}
            disabled={safePage === 1 || totalPages === 1}
          >
            {t("common.table.first")}
          </button>
          <button
            className="dt__btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage === 1 || totalPages === 1}
          >
            {t("common.table.prev")}
          </button>

          <span className="dt__pageInfo">
            {t("common.table.page")} <b>{safePage}</b> {t("common.table.of")}{" "}
            <b>{totalPages}</b>
          </span>

          <button
            className="dt__btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages || totalPages === 1}
          >
            {t("common.table.next")}
          </button>
          <button
            className="dt__btn"
            onClick={() => setPage(totalPages)}
            disabled={safePage === totalPages || totalPages === 1}
          >
            {t("common.table.last")}
          </button>
        </div>
      </div>
    </div>
  );
}
